import {RangeDatePicker} from '@hconnect/common/maintain-filters'
import {Dates} from '@hconnect/uikit'
import AddIcon from '@mui/icons-material/Add'
import CompressIcon from '@mui/icons-material/Compress'
import ExpandIcon from '@mui/icons-material/Expand'
import RemoveIcon from '@mui/icons-material/Remove'
import YoutubeSearchedForIcon from '@mui/icons-material/YoutubeSearchedFor'
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Button,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material'
import moment from 'moment'
import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {usePlantMonitoring} from '../context/PlantMonitoringContext'
import {useQuickSelectionItems} from '../hooks/useQuickSelectionItems'

export const ControlPanel = () => {
  return (
    <Box
      display="flex"
      flexGrow={1}
      justifyContent={'flex-end'}
      gap={1}
      flexDirection={{xs: 'column', sm: 'row'}}
    >
      <TimeRange />
      <Columns />
      <ScaleToThresholds />
      <ResetZoom />
    </Box>
  )
}

const Columns = () => {
  const {t} = useTranslation()
  const {state, actions} = usePlantMonitoring()
  const {columns} = state.chartLayout

  const chartsCount = state.signalCharts.length

  return (
    <Box
      display="flex"
      justifyContent={'space-between'}
      sx={{
        border: '1px solid rgba(255, 255, 255, 0.15)',
        borderRadius: '6px',
        maxHeight: '48px',
        height: '48px',
        flex: '0 1 150px',
        px: 0.5
      }}
      data-test-id="popout-columns"
    >
      <IconButton
        disabled={columns <= 1}
        onClick={() => actions.setColumns(columns - 1)}
        edge="end"
        sx={{color: 'common.white', alignSelf: 'center'}}
      >
        <RemoveIcon />
      </IconButton>
      <Stack alignItems="center" justifyContent="center">
        <Typography variant="caption" color="rgba(255, 255, 255, 0.60)">
          {t('chartSettings.columns')}
        </Typography>
        <Typography variant="h5">{columns}</Typography>
      </Stack>
      <IconButton
        onClick={() => actions.setColumns(columns + 1)}
        disabled={columns >= chartsCount}
        edge="end"
        sx={{color: 'common.white', alignSelf: 'center'}}
        data-test-id="add-column-btn"
      >
        <AddIcon />
      </IconButton>
    </Box>
  )
}

const ScaleToThresholds = () => {
  const {t} = useTranslation()
  const {
    state: {scaleToThresholds},
    actions: {setScaleToThresholds}
  } = usePlantMonitoring()

  return (
    <ToggleButtonGroup
      color="primary"
      exclusive
      value={scaleToThresholds}
      onChange={() => setScaleToThresholds(!scaleToThresholds)}
      sx={{
        background: '#01356A',
        display: 'flex',
        border: '1px solid rgba(255, 255, 255, 0.13)',
        height: '48px',
        '& .MuiToggleButton-root': {
          borderRadius: '6px !important',
          flexGrow: 1,
          ':not(.Mui-selected):hover': {
            background: 'transparent !important'
          }
        }
      }}
    >
      <ToggleButton
        value={true}
        aria-label={t('chartSettings.enable')}
        sx={{color: 'common.white'}}
      >
        <ExpandIcon sx={{color: 'common.white'}} />
      </ToggleButton>
      <ToggleButton value={false} aria-label={t('chartSettings.disable')}>
        <CompressIcon sx={{color: 'common.white'}} />
      </ToggleButton>
    </ToggleButtonGroup>
  )
}

const ResetZoom = () => {
  const {t} = useTranslation()
  const {actions, state} = usePlantMonitoring()

  return (
    <Button
      variant="contained"
      startIcon={<YoutubeSearchedForIcon />}
      disabled={!state.timeRange.zoomStart && !state.timeRange.zoomEnd}
      onClick={() => actions.setZoomRange(null, null)}
      sx={{
        flex: '0 1 150px',
        height: '48px',
        maxHeight: '48px',
        background: 'rgba(255, 255, 255, 0.05)',
        border: '1px solid rgba(255, 255, 255, 0.15)',
        boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.08)',
        borderRadius: '6px',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
          borderColor: 'rgba(255, 255, 255, 0.6)',
          boxShadow: 'none'
        },
        '&:disabled': {
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          borderColor: 'rgba(255, 255, 255, 0.15)',
          boxShadow: 'none',
          color: 'rgba(255, 255, 255, 0.5)'
        }
      }}
    >
      <Typography noWrap sx={{fontWeight: 500}}>
        {t('chartSettings.resetZoom')}
      </Typography>
    </Button>
  )
}

const TimeRange = () => {
  const {state, actions} = usePlantMonitoring()
  const {quickSelection, start, end} = state.timeRange
  const {selectedItem, selectionItems} = useQuickSelectionItems(quickSelection)

  const dateRange: [Date | null, Date | null] = useMemo(
    () => [start?.toDate() ?? null, end?.toDate() ?? null],
    [start, end]
  )

  const handleChange = (value: Date | Dates<Date>) => {
    if (value instanceof Date) {
      actions.setDateRange(moment(value).startOf('day'), moment(value).endOf('day'))
    } else if (value && value.startDate && value.endDate) {
      actions.setDateRange(moment(value.startDate), moment(value.endDate))
    }
  }

  return (
    <RangeDatePicker
      dateRange={dateRange}
      onChange={handleChange}
      quickSelectionItems={selectionItems}
      selectedItem={selectedItem}
      onQuickSelection={(item) =>
        item && actions.setQuickSelection(item.id!, moment(item.dates[0]), moment(item.dates[1]))
      }
      isOutsideRange={(day: Date) => {
        const selectedDay = moment(day).startOf('day')
        const today = moment().startOf('day')

        // Allow today and past dates, disable future dates (except today)
        return selectedDay.isAfter(today)
      }}
      DatePickerTextFieldProps={{
        sx: {
          border: '1px solid rgba(255, 255, 255, 0.15)',
          borderRadius: '6px',
          maxHeight: '48px',
          height: '48px',
          flex: '1 1 200px',
          '& .MuiTypography-h4': {color: 'white', fontSize: '15px'},
          '& .MuiSvgIcon-root': {color: 'white', fontSize: '15px'},
          '& .MuiTypography-caption': {color: 'rgba(255, 255, 255, 0.60)'}
        }
      }}
    />
  )
}
